import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';

// Data
import { useSelector, useDispatch } from 'react-redux';
import { getIdsRefreshing } from 'data/institutionLogins/selectors';
import { getScheduledTransactions } from 'data/scheduledTransactions/scheduledTransactionsActions';
import { getAccountFilteredBillsAndIncome, isLoading } from 'data/scheduledTransactions/scheduledTransactionsSelectors';

// CUSTOM
import QButton from 'components/QButton';
import QCheck from 'components/QCheck';
import RecurringPage from 'components/GettingStartedAcme/Recurring/RecurringPage';


const useStyles = makeStyles((theme) => ({
  root: {
    height: 500,
  },
  contentRoot: {
    margin: '32px 24px 24px',
    height: 359,
  },
  grey: {
    color: theme.palette.greyScale[1],
  },
  centeredSection: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  addSection: {
    width: '100%',
    height: '100%',
    borderRadius: 4,
    padding: '20px 24px 28px',
    backgroundColor: theme.palette.greyScale[6],
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  hiddenAdd: {
    opacity: 0,
    transition: 'opacity 1s ease 0.5s',
  },

  // Recurring stuff below
  recurringList: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    justifyContent: 'flex-start',
    alignItems: 'center',
    padding: '4px',
  },
  recurringCard: {
    width: 198,
    height: 260,
    flex: '0 0 auto',
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    alignItems: 'center',
  },
  bolder: {
    fontWeight: 500,
    color: theme.palette.greyScale[0],
  },
  centerWrapper: {
    height: 415,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
  },
  bottomRow: {
    padding: '12px 24px 24px',
    display: 'flex',
    justifyContent: 'center',
    height: 85,
  },
}));

export function BillScoutPresentPage(props) {
  const classes = useStyles();
  const { institutionLoginId, onClose } = props;
  const dispatch = useDispatch();
  const { income, bills } = useSelector((state) => getAccountFilteredBillsAndIncome(state, {institutionLoginId }));
  const scheduleLoading = useSelector(isLoading);
  const institutionsRefreshing = useSelector(getIdsRefreshing);
  const banksLoading = !institutionsRefreshing.isEmpty();
  const [loading, setLoading] = useState('init');
  const [state, setState] = useState('waiter');

  let showIncome = false;
  let showBills = false;
  if (!loading) {
    showIncome = income.size > 0;
    showBills = bills.size > 0;
    if (!showIncome && !showBills) {
      onClose();
    } else if (state === 'waiter') {
      setTimeout(() => {
        if (state === 'waiter') {
          if (showIncome) {
            setState('income');
          } else if (showBills) {
            setState('bills');
          } else {
            setState('final');
          }
        }
      }, 1800)
    }
  }

  useEffect(() => {
    if (loading === 'init' && banksLoading) {
      setLoading('agg');
    }
    if (loading === 'agg' && !banksLoading) {
      if (scheduleLoading) {
        setLoading('ST');
      } else {
        dispatch(getScheduledTransactions());
      }
    }
    if (loading === 'ST' && !scheduleLoading) {
      setLoading(false);
    }
  }, [loading, banksLoading, scheduleLoading]); // eslint-disable-line react-hooks/exhaustive-deps


  const proceed = () => {
    switch(state) {
      case 'waiter':
        if (showIncome) {
          setState('income');
        } else if (showBills) {
          setState('bills');
        } else {
          setState('final');
        }
        break;
      case 'income':
        if (showBills) {
          setState('bills');
        } else {
          setState('final');
        }
        break;
      case 'bills':
        setState('final');
        break;
      case 'final':
        onClose();
        break;
      default:
        setState('final');
    }
  };

  // # # - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - # # //
  //                                  Renderers                                      //
  // # # - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - # # //
  const renderBillScoutWaiter = () =>
    <div className={classes.centerWrapper} id="waiterSize">
      <Typography variant="h6">
        {loading ? 'Hang Tight!' : 'All Right!'}
      </Typography>
      <Typography variant="body1" className={classes.grey}>
        {loading ? "We're processing your transactions." : "We've got your data." }
      </Typography>
      <div className={classes.recurringList}>
        {loading ?
          <CircularProgress size={80} style={{ margin: 20 }}/>
          :
          <QCheck style={{ margin: 38}} size={44}/>
        }
        <Typography variant="body1" className={classes.grey}>
          {loading ? 'This should only take a few moments.' : `Let's continue`}
        </Typography>
      </div>
    </div>;

  const renderIncome = () =>
    <div className={classes.contentRoot}>
      <RecurringPage
        scheduledTxns={income}
      />
    </div>;

  const renderBills = () =>
    <div className={classes.contentRoot}>
      <RecurringPage
        scheduledTxns={bills}
        isBill
      />
    </div>;

  const renderFinalRedirect = () =>
    <div className={classes.centerWrapper}>
      <Typography variant="h6">
        {"You're all set!"}
      </Typography>
      <div className={classes.recurringList}>
        <QCheck style={{ margin: 16}} size={44}/>
      </div>
      <QButton
        variant="contained"
        onClick={onClose}
        style={{ marginTop: 6 }}
      >
        {`Let's Go!`}
      </QButton>
    </div>;

  // # # - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - # # //
  //                                   Main Return                                   //
  // # # - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - # # //
  return (
    <div className={classes.root}>
      {state === 'waiter' &&
        renderBillScoutWaiter()
      }
      {state === 'income' &&
        renderIncome()
      }
      {state === 'bills' &&
        renderBills()
      }
      {state === 'final' &&
        renderFinalRedirect()
      }
      <div className={classes.bottomRow}>
        <QButton
          variant="outlined"
          onClick={onClose}
        >
          Skip
        </QButton>
        <QButton
          variant="contained"
          onClick={proceed}
          disabled={Boolean(loading)}
          style={{ marginLeft: 24 }}
        >
          Continue
        </QButton>
      </div>
    </div>
  );

}

BillScoutPresentPage.propTypes={
  institutionLoginId: PropTypes.string,
  onClose: PropTypes.func,
};

export default BillScoutPresentPage;
